import { z } from 'zod';
import { APIFactory } from '@ninja/api-client';
import { convertNullToUndefined } from './convert-null-to-undefined';

export type APIFactoryOptions = {
	apiURL: string;
	appType?: 'admin' | 'customer';
	apiToken?: string | (() => Promise<string | undefined>);
	refreshToken?: () => Promise<string | undefined>;
	locale?: 'ar' | 'en';
};

export const factory = ({ apiURL, appType, apiToken, refreshToken, locale }: APIFactoryOptions) =>
	new APIFactory({
		baseUrl: apiURL,
		errorCodes: [
			'PaymentPending',
			'QuoteExpired',
			'QuoteAlreadyPurchased',
			'userIsBlackListed',
			'EffectiveDateFromIsInPastDate',
		],
		originalResponseSchema: z
			.object({
				data: z.record(z.string(), z.any()),
				errors: z.undefined().nullish(),
			})
			.or(
				z.object({
					data: z.undefined().nullish(),
					errors: z.array(z.object({ code: z.string() })),
				})
			),
		isErrorResponse: (options) => {
			const { headers, status } = options;

			return headers['is-blacklisted'] === 'true' || status >= 400;
		},
		transformResponse: (response) => {
			if (response.data) return convertNullToUndefined(response.data);
			else return convertNullToUndefined(response);
		},
		transformError: (response, status, url, headers) => {
			if (headers['is-blacklisted'] === 'true') {
				return { mapped: [{ code: 'userIsBlackListed' }] };
			}

			if (!response.errors) return undefined;

			return { mapped: response.errors, status, originalResponse: response };
		},
		refreshToken: refreshToken
			? async () => {
					const newToken = await refreshToken();

					if (newToken) return newToken;
			  }
			: undefined,
		headers: {
			'Content-Type': 'application/json',
			'Custom-Lang': locale,
			'client-source': appType === 'admin' ? 'shoryuser' : 'customer',
			Authorization:
				typeof apiToken === 'undefined'
					? undefined
					: typeof apiToken === 'function'
					? async () => {
							const token = await apiToken();

							if (token) return `Bearer ${token}`;
					  }
					: `Bearer ${apiToken}`,
		},
	});
